import React from 'react'
import { useIntl } from 'react-intl'
import { useHistory, useLocation } from 'react-router-dom'
import { spacing } from '@vacationtracker/shared/constants/ThemeSetting'

import { EmptyState } from '../../design-system/EmptyState'
import { SegmentedToggle } from '../../design-system/SegmentedToggle'

export type CatalogFilterValue = 'all' | 'active' | 'inactive'

const PARAM = 'use'
const FILTERS: CatalogFilterValue[] = ['all', 'active', 'inactive']

function readFilter(search: string): CatalogFilterValue {
  const asked = new URLSearchParams(search).get(PARAM) as CatalogFilterValue
  return FILTERS.includes(asked) ? asked : 'all'
}

/** The filter lives in the address, so a reload or a shared link keeps it. */
function useFilterParam(): [CatalogFilterValue, (next: CatalogFilterValue) => void] {
  const { search, pathname, hash } = useLocation()
  const history = useHistory()
  const set = (next: CatalogFilterValue) => {
    const params = new URLSearchParams(search)
    if (next === 'all') {
      params.delete(PARAM)
    } else {
      params.set(PARAM, next)
    }
    const query = params.toString()
    history.replace(`${pathname}${query ? `?${query}` : ''}${hash}`)
  }
  return [readFilter(search), set]
}

export interface CatalogFilterProps<T> {
  items: T[]
  isActive: (item: T) => boolean
  /** An item with no status shows under All only, and in neither count. */
  hasStatus: (item: T) => boolean
  emptyTitles: { active: string; inactive: string }
  render: (items: T[]) => React.ReactNode
}

/** All, Active and Inactive with counts over one list. A filter that empties the list says so and leads back to All. */
export function CatalogFilter<T>({ items, isActive, hasStatus, emptyTitles, render }: CatalogFilterProps<T>): React.ReactElement {
  const { formatMessage } = useIntl()
  const [filter, setFilter] = useFilterParam()
  const withStatus = items.filter(hasStatus)
  const active = withStatus.filter(isActive)
  const inactive = withStatus.filter((item) => !isActive(item))
  const shown = { all: items, active, inactive }[filter]

  return (
    <>
      <div style={{ marginBottom: spacing.base }}>
        <SegmentedToggle
          label={formatMessage({ id: 'catalogFilter.label' })}
          value={filter}
          onChange={(next) => setFilter(next as CatalogFilterValue)}
          options={[
            { value: 'all', label: formatMessage({ id: 'catalogFilter.all' }, { count: items.length }) },
            { value: 'active', label: formatMessage({ id: 'catalogFilter.active' }, { count: active.length }) },
            { value: 'inactive', label: formatMessage({ id: 'catalogFilter.inactive' }, { count: inactive.length }) },
          ]}
        />
      </div>
      {shown.length > 0 || filter === 'all' ? (
        render(shown)
      ) : (
        <EmptyState
          description={filter === 'active' ? emptyTitles.active : emptyTitles.inactive}
          action={{ label: formatMessage({ id: 'catalogFilter.showAll' }), onClick: () => setFilter('all') }}
        />
      )}
    </>
  )
}

export default CatalogFilter
